import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useAction } from "convex/react";
import { CheckCircle2Icon, CircleAlertIcon } from "lucide-react";
import { useRef } from "react";
import { useSearchParams } from "react-router";
import { Spinner } from "@/components/ui/spinner";
import { api } from "../../../../convex/_generated/api";
import {
	ANTHROPIC_OAUTH_POPUP_MESSAGE,
	publishAnthropicOAuthResult,
} from "./anthropicOAuthPopup";

function errorCode(error: unknown): string {
	if (!(error instanceof Error)) return "subscription.oauth.callback_failed";
	return error.message.startsWith("subscription.")
		? error.message.split(":", 1)[0]
		: "subscription.oauth.callback_failed";
}

export default function AnthropicOAuthCallbackPage() {
	const completeOAuth = useAction(
		api.llmSubscriptions.anthropicActions.completeOAuth,
	);
	const [searchParams] = useSearchParams();
	const code = searchParams.get("code") ?? "";
	const state = searchParams.get("state") ?? "";
	const startedRef = useRef(false);
	const { mutate, status, error } = useReactQueryMutation({
		mutationKey: ["anthropic-subscription-oauth", state, code],
		mutationFn: async () => {
			if (!code || !state) {
				throw new Error("subscription.oauth.callback_invalid");
			}
			await completeOAuth({ code, state });
		},
		onSuccess: () => {
			publishAnthropicOAuthResult(
				{
					type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
					status: "connected",
				},
				state,
			);
		},
		onError: (failure: unknown) => {
			publishAnthropicOAuthResult(
				{
					type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
					status: "error",
					code: errorCode(failure),
				},
				state,
			);
		},
	});
	const beginMutationRef = (node: HTMLDivElement | null) => {
		if (!node || startedRef.current) return;
		startedRef.current = true;
		mutate();
	};

	const failed = status === "error";
	const connected = status === "success";
	return (
		<main
			ref={beginMutationRef}
			className="flex min-h-[60vh] items-center justify-center p-6"
		>
			<div className="flex max-w-md flex-col items-center gap-3 text-center">
				{!failed && !connected ? <Spinner className="size-7" /> : null}
				{connected ? (
					<CheckCircle2Icon className="size-8 text-emerald-600" />
				) : null}
				{failed ? (
					<CircleAlertIcon className="size-8 text-destructive" />
				) : null}
				<h1 className="text-lg font-semibold">
					{failed
						? "Anthropic connection failed"
						: connected
							? "Anthropic account connected"
							: "Verifying Anthropic account"}
				</h1>
				<p className="text-sm text-muted-foreground">
					{failed
						? errorCode(error)
						: connected
							? "Close this window if it does not close automatically."
							: "Checking identity, models, quota, pricing, and prompt caching."}
				</p>
			</div>
		</main>
	);
}
